import { Sources, useTrackerApi } from "@/v2/entities/tracker/Tracker.api";
import { useQuery } from "react-query";
import { useState, useCallback, useEffect, useMemo } from "react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
  defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import {
  DateRange,
  buildRange,
} from "@/components/shared/date-range-picker/DateRangePicker";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import styles from "./trackerDetail.module.scss";
import TrackerItemTicketView from "@/features/tracker/TrackerItemTicketView";
import TrackerItemCard from "@/features/tracker/TrackerItemCard";
import { VirtualTrackerColumn } from "./components/VirtualTrackerColumn";
import { TrackerHeader } from "./components/TrackerHeader";
import { TrackerActionRow } from "./components/TrackerActionRow";
import { customColumnSort } from "./components/TrackerUtils";
import { TrackerItem } from "@/v2/entities/tracker/Tracker.model";
import TrackerItemConversationView from "@/features/tracker/TrackerItemConversationView";

function IndividualTracker() {
  const { trackerId } = useParams<{ trackerId: string }>();
  const navigate = useNavigate();
  const trackerApi = useTrackerApi();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor)
  );

  const {
    data: tracker,
    isLoading: isLoadingTracker,
    refetch,
  } = useQuery({
    queryKey: ["trackers", trackerId],
    queryFn: () => trackerApi.getTracker(trackerId ?? ""),
    enabled: !!trackerId,
    onError: () => toast.error("Failed to load tracker"),
    onSuccess: (data) => {
      if (!data) {
        navigate("/trackers");
      }
    },
  });

  const [dateRange, setDateRange] = useState<DateRange>(buildRange["14d"]());

  const {
    data: trackerItems,
    isLoading: isLoadingTrackerItems,
    refetch: refetchItems,
  } = useQuery({
    queryKey: ["trackerItems", trackerId, dateRange],
    queryFn: () =>
      trackerApi.getTrackerItems(
        trackerId ?? "",
        new Date(dateRange.dateFrom).getTime()
      ),
    enabled: !!tracker?.id,
    onError: () => toast.error("Failed to load items"),
  });

  const isLoading = isLoadingTracker || isLoadingTrackerItems;

  // Local UI state for optimistic updates
  const [items, setItems] = useState<TrackerItem[]>([]);
  useEffect(() => {
    if (trackerItems) {
      setItems(trackerItems);
    }
  }, [trackerItems]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [
    selectedTrackerItemInteractionType,
    setSelectedTrackerItemInteractionType,
  ] = useState<Sources | null>(null);
  const [
    selectedTrackerItemInteractionId,
    setSelectedTrackerItemInteractionId,
  ] = useState<string>();
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (tracker?.columns) {
      const ignored = tracker.columns.find((col) =>
        ["ignored", "ignore"].includes(col.name.toLowerCase())
      );
      setHiddenColumns(ignored ? new Set([ignored.name]) : new Set());
    }
  }, [tracker]);

  const trackerFieldList = useMemo(
    () => tracker?.fields.map((f) => f.field) ?? [],
    [tracker]
  );

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!tracker) return;
      navigate(`/trackers/${tracker.id}/edit`, { state: { from: "details" } });
    },
    [tracker, navigate]
  );

  const handleArchive = useCallback(async () => {
    if (!tracker) return;
    try {
      const isArchived = !tracker.isArchived;
      await trackerApi.archiveTracker(tracker.id, isArchived);
      await refetch();
      toast(isArchived ? "Tracker archived" : "Tracker unarchived");
    } catch {
      toast.error("Failed to update archive status");
    }
  }, [tracker, trackerApi, refetch]);

  const toggleColumnVisibility = useCallback((columnName: string) => {
    setHiddenColumns((prev) => {
      const newSet = new Set(prev);
      newSet.has(columnName)
        ? newSet.delete(columnName)
        : newSet.add(columnName);
      return newSet;
    });
  }, []);

  const onItemClick = (interactionId: string, intercationType: Sources) => {
    setSelectedTrackerItemInteractionId(interactionId);
    setSelectedTrackerItemInteractionType(intercationType);
  };

  const onItemUpdate = () => {
    refetchItems();
  };

  if (isLoading) {
    return (
      <div className={styles.trackerDetailContainer}>
        <TrackerSkeleton />
      </div>
    );
  }

  if (!tracker) {
    // Redirect to trackers list if tracker is not found
    navigate("/trackers");
    return null;
  }

  return (
    <div className={styles.trackerDetailContainer}>
      <TrackerHeader
        tracker={tracker}
        onEdit={handleEdit}
        onArchive={handleArchive}
      />
      <TrackerActionRow
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        columns={tracker.columns}
        hiddenColumns={hiddenColumns}
        onToggleColumn={toggleColumnVisibility}
        tracker={tracker}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(event) => setActiveId(event.active.id as string)}
        onDragEnd={(props) => {
          if (!props.over) return;
          const sourceId = String(props.active.id);
          const destColumnId = props.over.id.toString();
          // Optimistically update UI
          setItems((prev) =>
            prev.map((item) =>
              item.id === sourceId ? { ...item, columnId: destColumnId } : item
            )
          );
          // Fire-and-forget backend update
          trackerApi
            .updateTrackerItemColumn(tracker.id, sourceId, destColumnId)
            .catch(() => {
              toast.error("Failed to update tracker item");
              refetchItems();
            });
          setActiveId(null);
        }}
        onDragCancel={() => setActiveId(null)}
      >
        <div className={styles.trackerDetailColumns}>
          {tracker.columns
            .filter((col) => !hiddenColumns.has(col.name))
            .sort(customColumnSort)
            .map((column) => {
              // Choose between virtual and regular column based on performance needs
              const ColumnComponent = VirtualTrackerColumn;

              return (
                <ColumnComponent
                  key={column.id}
                  column={column}
                  trackerItems={items}
                  trackerId={trackerId ?? ""}
                  onItemClick={onItemClick}
                  onItemUpdate={onItemUpdate}
                  trackerColumns={tracker.columns}
                  trackerFields={trackerFieldList}
                  isLoading={isLoadingTrackerItems}
                  tracker={tracker}
                />
              );
            })}
        </div>
        <DragOverlay
          dropAnimation={{
            sideEffects: defaultDropAnimationSideEffects({
              styles: { active: { opacity: "0.8" } },
            }),
          }}
        >
          {activeId &&
            (() => {
              const activeItem = items.find((item) => item.id === activeId);
              return activeItem ? (
                <div className="transform rotate-2 scale-105">
                  <TrackerItemCard
                    trackerId={trackerId ?? ""}
                    item={activeItem}
                    onItemClick={() => {}} // Disabled during drag
                    onItemUpdate={() => {}} // Disabled during drag
                    trackerColumns={tracker?.columns ?? []}
                    trackerFields={trackerFieldList}
                  />
                </div>
              ) : null;
            })()}
        </DragOverlay>
      </DndContext>
      {selectedTrackerItemInteractionId &&
        selectedTrackerItemInteractionType === "tickets" && (
          <TrackerItemTicketView
            ticketId={selectedTrackerItemInteractionId}
            onClose={() => {
              setSelectedTrackerItemInteractionId(undefined);
              refetchItems();
            }}
          />
        )}
      {selectedTrackerItemInteractionId &&
        selectedTrackerItemInteractionType === "conversations" && (
          <TrackerItemConversationView
            conversationId={selectedTrackerItemInteractionId}
            onClose={() => {
              setSelectedTrackerItemInteractionId(undefined);
              refetchItems();
            }}
          />
        )}
    </div>
  );
}

function TrackerSkeleton() {
  return (
    <>
      <div className={styles.trackerDetailHeader}>
        <div className={styles.trackerDetailHeader__skeleton} />
      </div>
      <div className={styles.trackerDetailActionRow}>
        <div className={styles.trackerDetailActionRow__skeleton} />
      </div>
      <div className={styles.trackerDetailColumns}>
        {[...Array(3)].map((_, idx) => (
          <div key={idx} className={styles.trackerDetailColumn__skeleton} />
        ))}
      </div>
    </>
  );
}

export default IndividualTracker;
